import React, { useState, useEffect, useCallback } from 'react';
import {
  PlusIcon, TrashIcon, PencilIcon, ArrowDownTrayIcon,
  CheckCircleIcon, XCircleIcon, TagIcon, UsersIcon,
} from '@heroicons/react/24/outline';
import { adminAPI } from '../services/api';
import styles from './PromoCodesPage.module.css';

const TYPE_LABELS = {
  free_trial:       'Free Trial',
  discount_percent: 'Discount %',
  discount_fixed:   'Fixed Discount',
};

const PARTNER_TYPES = ['university', 'hospital', 'corporate', 'influencer', 'campaign', 'other'];

const EMPTY_FORM = {
  code: '', type: 'free_trial', value: '', partner_name: '',
  partner_type: 'corporate', description: '', max_uses: '', expires_at: '',
};

function StatusBadge({ active }) {
  return (
    <span className={active ? styles.badgeActive : styles.badgeInactive}>
      {active
        ? <><CheckCircleIcon style={{ width: 12, height: 12 }} /> Active</>
        : <><XCircleIcon style={{ width: 12, height: 12 }} /> Inactive</>}
    </span>
  );
}

function TypeBadge({ type }) {
  const colors = {
    free_trial: styles.typeFreeTrial,
    discount_percent: styles.typeDiscount,
    discount_fixed: styles.typeDiscount,
  };
  return <span className={`${styles.typeBadge} ${colors[type] || ''}`}>{TYPE_LABELS[type] || type}</span>;
}

function ValueDisplay({ type, value }) {
  if (type === 'free_trial') return <span className={styles.valueText}>{value} days free</span>;
  if (type === 'discount_percent') return <span className={styles.valueText}>{value}% off</span>;
  return <span className={styles.valueText}>${value} off</span>;
}

export default function PromoCodesPage() {
  const [codes, setCodes]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [showForm, setShowForm]       = useState(false);
  const [editing, setEditing]         = useState(null);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [saving, setSaving]           = useState(false);
  const [formError, setFormError]     = useState('');
  const [activeCode, setActiveCode]   = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [redLoading, setRedLoading]   = useState(false);
  const [redPage, setRedPage]         = useState(1);
  const [redTotal, setRedTotal]       = useState(0);
  const [redTotalPages, setRedTotalPages] = useState(1);
  const [exporting, setExporting]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminAPI.getPromoCodes();
      setCodes(r.promoCodes || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setFormError(''); setShowForm(true); };
  const openEdit   = (pc) => {
    setEditing(pc);
    setForm({
      code: pc.code, type: pc.type, value: pc.value,
      partner_name: pc.partner_name || '', partner_type: pc.partner_type || 'corporate',
      description: pc.description || '',
      max_uses: pc.max_uses ?? '',
      expires_at: pc.expires_at ? pc.expires_at.split('T')[0] : '',
    });
    setFormError('');
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.code || !form.value) return setFormError('Code and value are required.');
    setSaving(true); setFormError('');
    try {
      const payload = {
        ...form,
        value: parseFloat(form.value),
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        expires_at: form.expires_at || null,
      };
      if (editing) {
        await adminAPI.updatePromoCode(editing.id, payload);
      } else {
        await adminAPI.createPromoCode(payload);
      }
      setShowForm(false);
      load();
    } catch (e) { setFormError(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this promo code? This cannot be undone.')) return;
    await adminAPI.deletePromoCode(id);
    if (activeCode?.id === id) setActiveCode(null);
    load();
  };

  const handleToggle = async (pc) => {
    await adminAPI.updatePromoCode(pc.id, { ...pc, is_active: !pc.is_active });
    load();
  };

  const loadRedemptions = useCallback(async (id, page = 1) => {
    setRedLoading(true);
    try {
      const r = await adminAPI.getPromoRedemptions(id, page);
      setRedemptions(r.redemptions || []);
      setRedTotal(r.total || 0);
      setRedTotalPages(r.totalPages || 1);
      setRedPage(page);
    } finally { setRedLoading(false); }
  }, []);

  const handleSelectCode = (pc) => {
    setActiveCode(pc);
    loadRedemptions(pc.id, 1);
  };

  const handleExport = async () => {
    if (!activeCode) return;
    setExporting(true);
    try {
      const r = await adminAPI.exportPromoRedemptions(activeCode.id);
      const url = URL.createObjectURL(new Blob([r], { type: 'text/csv' }));
      const a = document.createElement('a'); a.href = url;
      a.download = `promo-${activeCode.code}-${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
    } finally { setExporting(false); }
  };

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Promo Codes</h1>
          <p className={styles.pageSub}>Create and manage promotional codes for partnerships and campaigns.</p>
        </div>
        <button className={styles.btnPrimary} onClick={openCreate}>
          <PlusIcon style={{ width: 16, height: 16 }} /> New Promo Code
        </button>
      </div>

      {error && <div className={styles.errorBanner}>{error}</div>}

      {/* ── Codes Table ── */}
      <div className={styles.card}>
        <div className={styles.tableWrap}>
          {loading ? (
            <div className={styles.skeleton}>
              {[...Array(4)].map((_, i) => <div key={i} className={styles.skeletonRow} />)}
            </div>
          ) : codes.length === 0 ? (
            <div className={styles.empty}>
              <TagIcon style={{ width: 40, height: 40, color: 'var(--text-muted)' }} />
              <p>No promo codes yet. Create your first one.</p>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Type</th>
                  <th>Benefit</th>
                  <th>Partner</th>
                  <th>Used / Limit</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.map(pc => (
                  <tr
                    key={pc.id}
                    className={activeCode?.id === pc.id ? styles.rowActive : styles.row}
                    onClick={() => handleSelectCode(pc)}
                  >
                    <td><span className={styles.codeChip}>{pc.code}</span></td>
                    <td><TypeBadge type={pc.type} /></td>
                    <td><ValueDisplay type={pc.type} value={pc.value} /></td>
                    <td>
                      <div className={styles.partnerName}>{pc.partner_name || '—'}</div>
                      {pc.partner_type && <div className={styles.partnerType}>{pc.partner_type}</div>}
                    </td>
                    <td>
                      <span className={styles.usageText}>
                        {pc.used_count} / {pc.max_uses ?? '∞'}
                      </span>
                    </td>
                    <td className={styles.muteText}>
                      {pc.expires_at ? new Date(pc.expires_at).toLocaleDateString('en-GB') : 'Never'}
                    </td>
                    <td><StatusBadge active={pc.is_active} /></td>
                    <td>
                      <div className={styles.actions} onClick={e => e.stopPropagation()}>
                        <button className={styles.iconBtn} onClick={() => openEdit(pc)} title="Edit">
                          <PencilIcon style={{ width: 15, height: 15 }} />
                        </button>
                        <button
                          className={`${styles.iconBtn} ${pc.is_active ? styles.iconBtnWarn : styles.iconBtnSuccess}`}
                          onClick={() => handleToggle(pc)}
                          title={pc.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {pc.is_active
                            ? <XCircleIcon style={{ width: 15, height: 15 }} />
                            : <CheckCircleIcon style={{ width: 15, height: 15 }} />}
                        </button>
                        <button className={`${styles.iconBtn} ${styles.iconBtnDanger}`} onClick={() => handleDelete(pc.id)} title="Delete">
                          <TrashIcon style={{ width: 15, height: 15 }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Redemptions Panel ── */}
      {activeCode && (
        <div className={styles.card} style={{ marginTop: 24 }}>
          <div className={styles.panelHeader}>
            <div>
              <h2 className={styles.panelTitle}>
                <UsersIcon style={{ width: 18, height: 18 }} /> {activeCode.code} — Redemptions
              </h2>
              <p className={styles.panelSub}>{redTotal} user{redTotal !== 1 ? 's' : ''} used this code</p>
            </div>
            <button className={styles.btnExport} onClick={handleExport} disabled={exporting || redTotal === 0}>
              <ArrowDownTrayIcon style={{ width: 15, height: 15 }} />
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
          </div>

          <div className={styles.tableWrap}>
            {redLoading ? (
              <div className={styles.skeleton}>
                {[...Array(3)].map((_, i) => <div key={i} className={styles.skeletonRow} />)}
              </div>
            ) : redemptions.length === 0 ? (
              <div className={styles.empty}>
                <p>No redemptions yet for this code.</p>
              </div>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Member</th>
                    <th>Mobile</th>
                    <th>Member Code</th>
                    <th>Redeemed At</th>
                    <th>Trial Start</th>
                    <th>Trial End</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {redemptions.map((r, i) => (
                    <tr key={i} className={styles.row}>
                      <td><span className={styles.memberName}>{r.first_name} {r.last_name}</span></td>
                      <td className={styles.muteText}>{r.mobile}</td>
                      <td><span className={styles.codeChip}>{r.member_code || '—'}</span></td>
                      <td className={styles.muteText}>{new Date(r.redeemed_at).toLocaleDateString('en-GB')}</td>
                      <td className={styles.muteText}>{r.trial_start ? new Date(r.trial_start).toLocaleDateString('en-GB') : '—'}</td>
                      <td className={styles.muteText}>{r.trial_end ? new Date(r.trial_end).toLocaleDateString('en-GB') : '—'}</td>
                      <td><StatusBadge active={r.membership_status === 'active'} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {redTotalPages > 1 && (
            <div className={styles.pagination}>
              <button className={styles.pageBtn} disabled={redPage <= 1} onClick={() => loadRedemptions(activeCode.id, redPage - 1)}>Prev</button>
              <span className={styles.pageInfo}>Page {redPage} of {redTotalPages}</span>
              <button className={styles.pageBtn} disabled={redPage >= redTotalPages} onClick={() => loadRedemptions(activeCode.id, redPage + 1)}>Next</button>
            </div>
          )}
        </div>
      )}

      {/* ── Create/Edit Modal ── */}
      {showForm && (
        <div className={styles.modalOverlay} onClick={() => setShowForm(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h2 className={styles.modalTitle}>{editing ? 'Edit Promo Code' : 'New Promo Code'}</h2>

            <form onSubmit={handleSave} className={styles.form}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Code *</label>
                  <input
                    className={styles.input}
                    value={form.code}
                    onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                    placeholder="e.g. AUB2026"
                    disabled={!!editing}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Type *</label>
                  <select className={styles.input} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} disabled={!!editing}>
                    <option value="free_trial">Free Trial (days)</option>
                    <option value="discount_percent">Discount (%)</option>
                    <option value="discount_fixed">Fixed Discount ($)</option>
                  </select>
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>
                    {form.type === 'free_trial' ? 'Trial Days *' : form.type === 'discount_percent' ? 'Discount % *' : 'Discount Amount *'}
                  </label>
                  <input
                    className={styles.input}
                    type="number"
                    min="1"
                    value={form.value}
                    onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
                    placeholder={form.type === 'free_trial' ? '30' : form.type === 'discount_percent' ? '25' : '10'}
                    disabled={!!editing}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Partner Type</label>
                  <select className={styles.input} value={form.partner_type} onChange={e => setForm(f => ({ ...f, partner_type: e.target.value }))}>
                    {PARTNER_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Partner / Campaign Name</label>
                <input className={styles.input} value={form.partner_name} onChange={e => setForm(f => ({ ...f, partner_name: e.target.value }))} placeholder="e.g. American University of Beirut" />
              </div>

              <div className={styles.formGroup}>
                <label>Description</label>
                <textarea className={styles.textarea} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Internal notes about this code..." rows={2} />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Max Uses (leave blank for unlimited)</label>
                  <input className={styles.input} type="number" min="1" value={form.max_uses} onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))} placeholder="Unlimited" />
                </div>
                <div className={styles.formGroup}>
                  <label>Expiry Date (leave blank for no expiry)</label>
                  <input className={styles.input} type="date" value={form.expires_at} onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} />
                </div>
              </div>

              {formError && <div className={styles.formError}>{formError}</div>}

              <div className={styles.formActions}>
                <button type="button" className={styles.btnSecondary} onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className={styles.btnPrimary} disabled={saving}>
                  {saving ? 'Saving...' : editing ? 'Save Changes' : 'Create Code'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
